import React from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@mui/material";

import NearMeIcon from "@mui/icons-material/NearMe";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import { usePolygonDrawContext } from "./PolygonDrawContext";
import { DRAW_MODES } from "./helpers";

import styles from "./PolygonToolbar.module.css";

const ACTIVE_SX = {
  color: "primary.main",
  backgroundColor: "rgba(25, 118, 210, 0.12)",
};

export default function PolygonToolbar() {
  const { t } = useTranslation();
  const {
    isActive,
    results,
    mode,
    selectedIds,
    startSelecting,
    startDrawing,
    startEditing,
    deleteSelected,
    undo,
    redo,
    canUndo,
    canRedo,
  } = usePolygonDrawContext();

  // While a result is shown the polygons are locked (STATIC mode) — hide the
  // editing toolbar so no changes can be made until "Adjust & re-assess".
  if (!isActive || results) return null;

  const canEdit = selectedIds.length === 1;
  const canDelete = selectedIds.length > 0;

  return (
    <div className={styles.toolbar}>
      <Tooltip title={t("risksMap.polygon.tools.select")} placement="left">
        <IconButton
          size="small"
          onClick={startSelecting}
          sx={mode === DRAW_MODES.SELECT ? ACTIVE_SX : undefined}
          aria-label={t("risksMap.polygon.tools.select")}
        >
          <NearMeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("risksMap.polygon.tools.draw")} placement="left">
        <IconButton
          size="small"
          onClick={startDrawing}
          sx={mode === DRAW_MODES.DRAW ? ACTIVE_SX : undefined}
          aria-label={t("risksMap.polygon.tools.draw")}
        >
          <PentagonOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("risksMap.polygon.tools.edit")} placement="left">
        <span>
          <IconButton
            size="small"
            onClick={startEditing}
            disabled={!canEdit}
            sx={mode === DRAW_MODES.EDIT ? ACTIVE_SX : undefined}
            aria-label={t("risksMap.polygon.tools.edit")}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={t("risksMap.polygon.tools.delete")} placement="left">
        <span>
          <IconButton
            size="small"
            onClick={deleteSelected}
            disabled={!canDelete}
            aria-label={t("risksMap.polygon.tools.delete")}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <div className={styles.divider} />

      <Tooltip title={t("risksMap.polygon.tools.undo")} placement="left">
        <span>
          <IconButton
            size="small"
            onClick={undo}
            disabled={!canUndo}
            aria-label={t("risksMap.polygon.tools.undo")}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={t("risksMap.polygon.tools.redo")} placement="left">
        <span>
          <IconButton
            size="small"
            onClick={redo}
            disabled={!canRedo}
            aria-label={t("risksMap.polygon.tools.redo")}
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
